import { cn } from '@/lib/utils';
import { DockLeftIcon, DockRightIcon } from '../icons';
import type { DockSide, ModalPlacement } from './useModalPlacement';

export const DOCK_LEFT_LABEL = 'Dock to left edge';
export const DOCK_RIGHT_LABEL = 'Dock to right edge';
export const UNDOCK_TITLE = 'Back to centre';

interface ModalDockControlsProps {
  placement: ModalPlacement;
  onDock: (side: DockSide) => void;
  className?: string;
}

/**
 * The two one-click alternatives to dragging (MPN-24): park the modal at the
 * left or right edge of the viewport so the page behind can be read. Pressing
 * the active edge again re-centres. Dragging is undiscoverable on its own;
 * these buttons are what staff will actually find.
 */
export function ModalDockControls({
  placement,
  onDock,
  className,
}: ModalDockControlsProps) {
  return (
    <div className={cn('flex items-center gap-0.5', className)}>
      <DockButton
        side="left"
        label={DOCK_LEFT_LABEL}
        pressed={placement.dock === 'left'}
        onDock={onDock}
      />
      <DockButton
        side="right"
        label={DOCK_RIGHT_LABEL}
        pressed={placement.dock === 'right'}
        onDock={onDock}
      />
    </div>
  );
}

interface DockButtonProps {
  side: DockSide;
  label: string;
  pressed: boolean;
  onDock: (side: DockSide) => void;
}

function DockButton({ side, label, pressed, onDock }: DockButtonProps) {
  const Icon = side === 'left' ? DockLeftIcon : DockRightIcon;
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={pressed}
      title={pressed ? UNDOCK_TITLE : label}
      onClick={() => onDock(side)}
      className={cn(
        'rounded-[6px] p-1 transition-colors',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-6',
        pressed ? 'text-primary-6' : 'text-neutral-n500 hover:text-neutral-n800'
      )}
    >
      <Icon size={20} />
    </button>
  );
}
